import type { SupportedLanguage } from "@amtana/i18n/catalog";
import type { Post, PostFile } from "virtual:blog-posts";

// Which posts a reader sees, and in what order (ADR-029 point 3). Pure: it takes the manifest, so it
// tests with no build. Only a language's own file ever counts, so a post in one language shows in
// that language's index and related posts alone.

/** The address of a post, with no language prefix. */
export function postPath(slug: string): `/${string}` {
  return `/blog/${slug}`;
}

/** The hand-picked list that sits on top of the index: an editorial call, never a metric. */
export const FEATURED_SLUGS: readonly string[] = ["what-are-instagram-leads"];

const RELATED_LIMIT = 3;

export type ListedPost = { readonly slug: string; readonly file: PostFile; readonly featured: boolean };

const time = (file: PostFile): number => (file.publishedAt ? Date.parse(file.publishedAt) : Infinity);

/** A language's posts, newest first. A draft, which only `pnpm dev` holds, sorts ahead of them. */
export function postsIn(
  posts: readonly Post[],
  language: SupportedLanguage,
  featured: readonly string[] = FEATURED_SLUGS,
): readonly ListedPost[] {
  return posts
    .flatMap((post) => {
      const file = post.languages[language];
      return file ? [{ slug: post.slug, file, featured: featured.includes(post.slug) }] : [];
    })
    .toSorted((a, b) => time(b.file) - time(a.file));
}

/** The index: the featured posts on top, each group newest first. */
export function indexOf(listed: readonly ListedPost[]): readonly ListedPost[] {
  return [...listed.filter((post) => post.featured), ...listed.filter((post) => !post.featured)];
}

/** Up to three others of the same category and language, newest first. */
export function relatedPosts(
  posts: readonly Post[],
  slug: string,
  language: SupportedLanguage,
): readonly ListedPost[] {
  const category = posts.find((post) => post.slug === slug)?.languages[language]?.category;
  return postsIn(posts, language)
    .filter((post) => post.slug !== slug && post.file.category === category)
    .slice(0, RELATED_LIMIT);
}

/** The categories a language's posts use, in the order they first appear. */
export function categoriesOf(listed: readonly ListedPost[]): readonly string[] {
  return [...new Set(listed.map((post) => post.file.category))];
}
